import { useEffect, useId, useRef, useState } from 'react';

import { useForm } from '@tanstack/react-form';
import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import {
  Button,
  FormField,
  Modal,
  Spinner,
  SubmitButtonWrapper,
  TextBlock,
  Title,
  toast,
} from '@skillarea/lib-frontend';
import {
  CRYPTOCLOUD_USDT_NETWORKS,
  CARD_DEPOSIT_ASYNC_PROCESSING_RESULT,
  EDodoDepositPaymentMethod,
  MIN_DEPOSIT_COINS,
  calculateDodoTotalWithServiceFeeCoins,
  calculatePaypalTotalWithServiceFeeCoins,
  detectVisa2PayCardType,
  formatMoney,
  isValidCardDepositExpiryMonth,
  isValidCardDepositExpiryYearUi,
  isValidCardNumberLuhn,
  normalizeCardDepositCardDetails,
} from '@skillarea/lib-shared';
import { Trans, useTranslation } from 'react-i18next';

import cx from 'classnames';

import {
  DEFAULT_NETWORK,
  getAccountRedirectUrl,
  getResultDataString,
  getResultErrorMessage,
  NetworkSelect,
  parseBillingTaskIdFromPayUrl,
  toCoinsNumber,
  validateDepositAmountCoins,
} from '../-acquiring-shared';
import { accountAuthGuard } from '../-account-auth-guard';
import styles from '../acquiring.module.scss';

import {
  DepositAmountSection,
  type TDepositAmountForm,
} from './-DepositAmountSection';
import { CardDepositFormSection } from './-CardDepositFormSection';

import { AccountLayout } from '~/components/Account/AccountLayout';
import { LayoutCard } from '~/components/LayoutCard/LayoutCard';
import { MinimumAgeModal } from '~/components/MinimumAgeModal/MinimumAgeModal';
import { useMinimumAgeGuard } from '~/hooks/useMinimumAgeGuard';
import { usePaypalDepositReturnCapture } from '~/hooks/usePaypalDepositReturnCapture';
import { usePersistCardDepositFormDraft } from '~/hooks/usePersistCardDepositFormDraft';
import {
  PAYMENT_SUCCESS_HASH,
  PENDING_DEPOSIT_STORAGE_KEY,
  getNormalizedPaymentHash,
  recordCryptoDepositAmountHintForTransactionId,
  recordLastCryptoDepositIntent,
  recordCardDepositAmountHintForTransactionId,
  recordLastCardDepositIntent,
} from '~/lib/billing/payment-return';
import {
  buildCardDepositFormDefaultValues,
  saveCardDepositFormDraft,
} from '~/lib/billing/card-deposit-form-draft';
import { BillingTaskService } from '~/services/billingTaskService';
import { useUserFlags, useUserStore } from '~/stores/userStore';
import { translateBillingError } from '~/lib/billingLocale';

const billingTaskService = new BillingTaskService();
const rootRouteApi = getRouteApi('__root__');

const DODO_DEPOSIT_UI_ENABLED = false;

const DODO_DEPOSIT_UI_METHODS = [
  'card',
  'google_pay',
  'apple_pay',
  'amazon_pay',
  'we_chat_pay',
] as const;

type TDodoDepositUiMethod = (typeof DODO_DEPOSIT_UI_METHODS)[number];
type TDepositPaymentMethod = TDodoDepositUiMethod | 'paypal' | 'crypto';

const UI_TO_DODO_PAYMENT_METHOD: Record<
  TDodoDepositUiMethod,
  EDodoDepositPaymentMethod
> = {
  card: EDodoDepositPaymentMethod.card,
  google_pay: EDodoDepositPaymentMethod.googlePay,
  apple_pay: EDodoDepositPaymentMethod.applePay,
  amazon_pay: EDodoDepositPaymentMethod.amazonPay,
  we_chat_pay: EDodoDepositPaymentMethod.weChatPay,
};

function isDodoDepositPaymentMethod(
  method: TDepositPaymentMethod,
): method is TDodoDepositUiMethod {
  return (DODO_DEPOSIT_UI_METHODS as readonly string[]).includes(method);
}

const DEPOSIT_PAYMENT_METHOD_OPTIONS = [
  {
    value: 'card' as const,
    labelKey: 'deposit.card' as const,
    submitKey: 'deposit.createCardDeposit' as const,
    testId: 'balance-deposit-method-card',
  },
  {
    value: 'google_pay' as const,
    labelKey: 'deposit.googlePay' as const,
    submitKey: 'deposit.createGooglePayDeposit' as const,
    testId: 'balance-deposit-method-google-pay',
  },
  {
    value: 'apple_pay' as const,
    labelKey: 'deposit.applePay' as const,
    submitKey: 'deposit.createApplePayDeposit' as const,
    testId: 'balance-deposit-method-apple-pay',
  },
  {
    value: 'amazon_pay' as const,
    labelKey: 'deposit.amazonPay' as const,
    submitKey: 'deposit.createAmazonPayDeposit' as const,
    testId: 'balance-deposit-method-amazon-pay',
  },
  {
    value: 'we_chat_pay' as const,
    labelKey: 'deposit.weChatPay' as const,
    submitKey: 'deposit.createWeChatPayDeposit' as const,
    testId: 'balance-deposit-method-wechat-pay',
  },
  {
    value: 'crypto' as const,
    labelKey: 'deposit.crypto' as const,
    submitKey: 'deposit.createCryptoDeposit' as const,
    testId: 'balance-deposit-method-crypto',
  },
  {
    value: 'paypal' as const,
    labelKey: 'deposit.paypal' as const,
    submitKey: 'deposit.createPaypalDeposit' as const,
    testId: 'balance-deposit-method-paypal',
  },
] as const;

const DEPOSIT_PAYMENT_METHOD_UI_ORDER: TDepositPaymentMethod[] = [
  'paypal',
  'card',
  'crypto',
  'google_pay',
  'apple_pay',
  'amazon_pay',
  'we_chat_pay',
];

const DEPOSIT_PAYMENT_METHOD_OPTIONS_FOR_UI =
  DEPOSIT_PAYMENT_METHOD_OPTIONS.filter(
    (option) =>
      option.value === 'card' ||
      option.value === 'paypal' ||
      option.value === 'crypto' ||
      (DODO_DEPOSIT_UI_ENABLED && isDodoDepositPaymentMethod(option.value)),
  ).sort(
    (left, right) =>
      DEPOSIT_PAYMENT_METHOD_UI_ORDER.indexOf(left.value) -
      DEPOSIT_PAYMENT_METHOD_UI_ORDER.indexOf(right.value),
  );

export const Route = createFileRoute('/account/balance/deposit')({
  component: DepositPage,
  beforeLoad: ({ context }) => accountAuthGuard({ context }),
});

function DepositPage() {
  const { t } = useTranslation('billing');
  const { user: routeUser } = rootRouteApi.useRouteContext();
  const userFlags = useUserFlags();
  const loadUser = useUserStore((state) => state.loadUser);
  const {
    closeMinimumAgeModal,
    isMinimumAgeModalOpen,
    isUnderMinimumAge,
    minimumAge,
    showMinimumAgeModal,
  } = useMinimumAgeGuard();
  const [lastPaymentUrl, setLastPaymentUrl] = useState<string>('');
  const [lastCardPaymentUrl, setLastCardPaymentUrl] = useState<string>('');
  const [lastDodoPaymentUrl, setLastDodoPaymentUrl] = useState<string>('');
  const [lastPaypalApprovalUrl, setLastPaypalApprovalUrl] =
    useState<string>('');
  const isPaypalCaptureProcessing = usePaypalDepositReturnCapture();
  const [paymentMethod, setPaymentMethod] =
    useState<TDepositPaymentMethod>('paypal');
  const paymentMethodRef = useRef<TDepositPaymentMethod>('paypal');
  const depositPaymentTypeLabelId = useId();
  const firstDepositBonusProcessed =
    userFlags.firstDepositBonusProcessed ??
    routeUser?.flags?.firstDepositBonusProcessed;
  const isFirstDepositBonusVisible = firstDepositBonusProcessed !== true;
  const minimumDepositLabel = formatMoney(MIN_DEPOSIT_COINS, { maxDigits: 0 });
  const [hasDepositAmountError, setHasDepositAmountError] =
    useState<boolean>(false);

  const clearDepositAmountError = (): void => {
    setHasDepositAmountError(false);
  };

  paymentMethodRef.current = paymentMethod;

  const showDepositAmountValidationError = (
    coins: number | null,
  ): coins is number => {
    if (coins === null) {
      setHasDepositAmountError(true);
      toast?.error(t('deposit.toast.amountPositive'), { skipDedupe: true });
      return false;
    }

    if (!validateDepositAmountCoins(coins)) {
      setHasDepositAmountError(true);
      toast?.error(
        t('deposit.toast.minimumAmount', { amount: minimumDepositLabel }),
        { skipDedupe: true },
      );
      return false;
    }

    setHasDepositAmountError(false);
    return true;
  };

  const depositForm = useForm({
    defaultValues: {
      coins: 0,
      currencyCode: DEFAULT_NETWORK,
      redirectUrl: '',
    },
    onSubmit: async ({ value }) => {
      if (isUnderMinimumAge) {
        showMinimumAgeModal();
        return;
      }

      const coins = toCoinsNumber(value.coins);
      if (!showDepositAmountValidationError(coins)) {
        return;
      }

      const result = await billingTaskService.createCryptoDepositTask({
        body: {
          coins,
          currencyCode: String(value.currencyCode),
          redirectUrl: getAccountRedirectUrl('/account/balance'),
        },
      });

      if (!result.success) {
        toast?.error(translateBillingError(t, getResultErrorMessage(result)));
        return;
      }

      const paymentUrl = getResultDataString(result);
      if (!paymentUrl) {
        toast?.error(t('deposit.toast.depositLinkMissing'));
        return;
      }

      setLastPaymentUrl(paymentUrl);

      const realId = parseBillingTaskIdFromPayUrl(paymentUrl);
      if (realId) {
        const transactionId = `cc-${realId}`;
        window.localStorage.setItem(
          PENDING_DEPOSIT_STORAGE_KEY,
          JSON.stringify({
            valueInCents: coins,
            currency: 'USD',
            transaction_id: transactionId,
          }),
        );
        recordCryptoDepositAmountHintForTransactionId(
          transactionId,
          coins,
          'USD',
        );
      } else {
        recordLastCryptoDepositIntent(coins, 'USD');
      }

      toast?.success(t('deposit.toast.depositCreated'));

      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.assign(paymentUrl);
        }, 0);
      }
    },
  });

  const dodoDepositForm = useForm({
    defaultValues: {
      coins: 0,
    },
    onSubmit: async ({ value }) => {
      if (isUnderMinimumAge) {
        showMinimumAgeModal();
        return;
      }

      const coins = toCoinsNumber(value.coins);
      if (!showDepositAmountValidationError(coins)) {
        return;
      }

      const totalCoinsWithServiceFee =
        calculateDodoTotalWithServiceFeeCoins(coins);
      if (totalCoinsWithServiceFee === undefined) {
        toast?.error(t('deposit.toast.amountPositive'));
        return;
      }

      const uiMethod = paymentMethodRef.current;
      if (!isDodoDepositPaymentMethod(uiMethod)) {
        return;
      }

      const result = await billingTaskService.createCardDepositTask({
        body: {
          coins,
          paymentMethod: UI_TO_DODO_PAYMENT_METHOD[uiMethod],
          redirectUrl: getAccountRedirectUrl('/account/balance'),
          billing: buildCardDepositFormDefaultValues().billing,
          card: buildCardDepositFormDefaultValues().card,
        },
      });

      if (!result.success) {
        toast?.error(translateBillingError(t, getResultErrorMessage(result)));
        return;
      }

      const paymentUrl = getResultDataString(result);
      if (!paymentUrl) {
        toast?.error(t('deposit.toast.depositLinkMissing'));
        return;
      }

      setLastDodoPaymentUrl(paymentUrl);

      const realId = parseBillingTaskIdFromPayUrl(paymentUrl);
      if (realId) {
        const transactionId = `dodo-${realId}`;
        window.localStorage.setItem(
          PENDING_DEPOSIT_STORAGE_KEY,
          JSON.stringify({
            valueInCents: totalCoinsWithServiceFee,
            currency: 'USD',
            transaction_id: transactionId,
          }),
        );
        recordCardDepositAmountHintForTransactionId(
          transactionId,
          totalCoinsWithServiceFee,
          'USD',
        );
      } else {
        recordLastCardDepositIntent(totalCoinsWithServiceFee, 'USD');
      }

      toast?.success(t('deposit.toast.depositCreated'));

      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.assign(paymentUrl);
        }, 0);
      }
    },
  });

  const cardDepositForm = useForm({
    defaultValues: buildCardDepositFormDefaultValues(),
    onSubmit: async ({ value }) => {
      if (isUnderMinimumAge) {
        showMinimumAgeModal();
        return;
      }

      const coins = toCoinsNumber(value.coins);
      if (!showDepositAmountValidationError(coins)) {
        return;
      }

      const totalCoinsWithServiceFee =
        calculateDodoTotalWithServiceFeeCoins(coins);
      if (totalCoinsWithServiceFee === undefined) {
        toast?.error(t('deposit.toast.amountPositive'));
        return;
      }

      if (!isValidCardDepositExpiryMonth(value.card.cardExpMonth)) {
        toast?.error(t('deposit.toast.invalidCardExpiryMonth'));
        return;
      }
      if (!isValidCardDepositExpiryYearUi(value.card.cardExpYear)) {
        toast?.error(t('deposit.toast.invalidCardExpiryYear'));
        return;
      }
      if (!isValidCardNumberLuhn(value.card.cardNumber)) {
        toast?.error(t('deposit.toast.invalidCardNumber'));
        return;
      }

      const normalizedCard = normalizeCardDepositCardDetails({
        ...value.card,
        cardType:
          detectVisa2PayCardType(value.card.cardNumber) ?? value.card.cardType,
      });
      if (!detectVisa2PayCardType(normalizedCard.cardNumber)) {
        toast?.error(t('deposit.toast.unsupportedCardType'));
        return;
      }

      saveCardDepositFormDraft({
        billing: value.billing,
        card: normalizedCard,
      });

      const result = await billingTaskService.createCardDepositTask(
        {
          body: {
            coins,
            redirectUrl: getAccountRedirectUrl('/account/balance'),
            billing: {
              ...value.billing,
              country: value.billing.country.trim().toUpperCase(),
            },
            card: normalizedCard,
          },
        },
        { timeout: 90_000 },
      );

      if (!result.success) {
        toast?.error(translateBillingError(t, getResultErrorMessage(result)));
        return;
      }

      const paymentUrl = getResultDataString(result);
      if (paymentUrl === CARD_DEPOSIT_ASYNC_PROCESSING_RESULT) {
        recordLastCardDepositIntent(totalCoinsWithServiceFee, 'USD');
        toast?.success(t('deposit.toast.cardDepositProcessing'));
        return;
      }

      if (paymentUrl) {
        setLastCardPaymentUrl(paymentUrl);

        window.localStorage.setItem(
          PENDING_DEPOSIT_STORAGE_KEY,
          JSON.stringify({
            valueInCents: totalCoinsWithServiceFee,
            currency: 'USD',
            transaction_id: `card-${Date.now()}`,
          }),
        );
        recordLastCardDepositIntent(totalCoinsWithServiceFee, 'USD');

        toast?.success(t('deposit.toast.depositCreated'));

        if (typeof window !== 'undefined') {
          setTimeout(() => {
            window.location.assign(paymentUrl);
          }, 0);
        }
        return;
      }

      recordLastCardDepositIntent(totalCoinsWithServiceFee, 'USD');
      toast?.success(t('deposit.toast.cardDepositCompleted'));
      await loadUser();
    },
  });

  usePersistCardDepositFormDraft(cardDepositForm);

  const paypalDepositForm = useForm({
    defaultValues: {
      coins: 0,
    },
    onSubmit: async ({ value }) => {
      if (isUnderMinimumAge) {
        showMinimumAgeModal();
        return;
      }

      const coins = toCoinsNumber(value.coins);
      if (!showDepositAmountValidationError(coins)) {
        return;
      }

      const totalCoinsWithServiceFee =
        calculatePaypalTotalWithServiceFeeCoins(coins);
      if (totalCoinsWithServiceFee === undefined) {
        setHasDepositAmountError(true);
        toast?.error(t('deposit.toast.amountPositive'), { skipDedupe: true });
        return;
      }

      const result = await billingTaskService.createPaypalDepositTask({
        body: {
          coins,
          redirectUrl: getAccountRedirectUrl('/account/balance'),
        },
      });

      if (!result.success) {
        toast?.error(translateBillingError(t, getResultErrorMessage(result)));
        return;
      }

      const approvalUrl = getResultDataString(result);
      if (!approvalUrl) {
        toast?.error(t('deposit.toast.paypalLinkMissing'));
        return;
      }

      setLastPaypalApprovalUrl(approvalUrl);

      let orderId = '';
      try {
        orderId = new URL(approvalUrl).searchParams.get('token') || '';
      } catch {
        const match = approvalUrl.match(/[?&]token=([^&]+)/);
        orderId = match ? match[1] : '';
      }

      if (orderId) {
        window.localStorage.setItem(
          PENDING_DEPOSIT_STORAGE_KEY,
          JSON.stringify({
            valueInCents: totalCoinsWithServiceFee,
            currency: 'USD',
            transaction_id: `pp-${orderId}`,
          }),
        );
      }

      toast?.success(t('deposit.toast.paypalCreated'));

      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.assign(approvalUrl);
        }, 0);
      }
    },
  });

  useEffect(() => {
    if (isUnderMinimumAge) {
      showMinimumAgeModal();
    }
  }, [isUnderMinimumAge, showMinimumAgeModal]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (
      getNormalizedPaymentHash(window.location.hash) === PAYMENT_SUCCESS_HASH
    ) {
      void loadUser();
    }
  }, [loadUser]);

  const isDepositBlockedByAge = isUnderMinimumAge;
  const isPaypalDeposit = paymentMethod === 'paypal';
  const isCardDeposit = paymentMethod === 'card';
  const isDodoDeposit = isDodoDepositPaymentMethod(paymentMethod);
  const selectedPaymentOption =
    DEPOSIT_PAYMENT_METHOD_OPTIONS.find(
      (option) => option.value === paymentMethod,
    ) ?? DEPOSIT_PAYMENT_METHOD_OPTIONS_FOR_UI[0];
  const activeDepositForm = isPaypalDeposit
    ? paypalDepositForm
    : isCardDeposit
      ? cardDepositForm
      : isDodoDeposit
        ? dodoDepositForm
        : depositForm;

  const handlePaymentMethodChange = (
    nextPaymentMethod: TDepositPaymentMethod,
  ): void => {
    if (nextPaymentMethod === paymentMethod) {
      return;
    }

    clearDepositAmountError();

    const sourceCoins = Number(activeDepositForm.store.state.values.coins);

    if (nextPaymentMethod === 'paypal') {
      paypalDepositForm.setFieldValue('coins', sourceCoins);
    } else if (nextPaymentMethod === 'card') {
      cardDepositForm.setFieldValue('coins', sourceCoins);
    } else if (isDodoDepositPaymentMethod(nextPaymentMethod)) {
      dodoDepositForm.setFieldValue('coins', sourceCoins);
    } else {
      depositForm.setFieldValue('coins', sourceCoins);
    }

    setPaymentMethod(nextPaymentMethod);
  };

  const paymentTypeField = (
    <div className={styles.field}>
      <TextBlock color="contrast" id={depositPaymentTypeLabelId} weight="600">
        {t('deposit.choosePaymentType')}
      </TextBlock>
      <div
        aria-labelledby={depositPaymentTypeLabelId}
        className={styles.paymentMethodRadioGroup}
        role="radiogroup"
      >
        {DEPOSIT_PAYMENT_METHOD_OPTIONS_FOR_UI.map((option) => (
          <label className={styles.paymentMethodRadioOption} key={option.value}>
            <input
              checked={paymentMethod === option.value}
              className={styles.paymentMethodRadioInput}
              data-testid={option.testId}
              disabled={isDepositBlockedByAge}
              name="deposit-payment-method"
              onChange={() => {
                handlePaymentMethodChange(option.value);
              }}
              type="radio"
              value={option.value}
            />
            <TextBlock color="contrast" weight="600">
              {t(option.labelKey)}
            </TextBlock>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <AccountLayout className={styles.content}>
      {isDepositBlockedByAge ? (
        <LayoutCard title={t('deposit.ageRestrictionTitle')}>
          <TextBlock color="contrast" weight="600">
            {t('deposit.ageRestrictionText')}
          </TextBlock>
        </LayoutCard>
      ) : null}

      {!isDepositBlockedByAge && isFirstDepositBonusVisible ? (
        <section
          className={styles.firstDepositBonusBanner}
          data-testid="first-deposit-bonus-banner"
        >
          <div className={styles.firstDepositBonusTextBox}>
            <Title
              className={styles.firstDepositBonusText}
              color="contrast"
              size="m"
              variant="benzin"
            >
              <Trans
                components={{
                  primary: <span className={styles.firstDepositBonusPrimary} />,
                }}
                i18nKey="deposit.firstDepositBonusText"
                ns="billing"
              />
            </Title>
          </div>
        </section>
      ) : null}

      <LayoutCard data-testid="account-deposit-page" title={t('deposit.title')}>
        {isPaypalDeposit ? (
          <form
            className={cx(
              styles.formCard,
              isDepositBlockedByAge && styles.blockedForm,
            )}
            data-testid="deposit-form"
            onSubmit={(e) => {
              e.preventDefault();
              void paypalDepositForm.handleSubmit();
            }}
          >
            <fieldset
              className={styles.formFieldset}
              disabled={isDepositBlockedByAge}
            >
              <DepositAmountSection
                amountLabel={t('deposit.amountUsd')}
                disabled={isDepositBlockedByAge}
                form={paypalDepositForm as TDepositAmountForm}
                hasAmountError={hasDepositAmountError}
                minimumDepositLabel={minimumDepositLabel}
                onAmountChange={clearDepositAmountError}
              />

              {paymentTypeField}

              <div className={styles.field}>
                <TextBlock color="contrast" weight="600">
                  {t('deposit.totalWithFee')}
                </TextBlock>
                <paypalDepositForm.Field name="coins">
                  {(field) => {
                    const totalCoinsWithServiceFee =
                      calculatePaypalTotalWithServiceFeeCoins(
                        toCoinsNumber(field.state.value),
                      );

                    return (
                      <>
                        <TextBlock
                          color="contrast"
                          data-testid="balance-deposit-total-display"
                          size="xl"
                          weight="600"
                        >
                          {formatMoney(totalCoinsWithServiceFee, {
                            nullValue: '$0.00',
                          })}
                        </TextBlock>
                        <TextBlock
                          color="secondary"
                          data-testid="balance-deposit-total-note"
                          size="s"
                        >
                          {t('deposit.totalFeeNote')}
                        </TextBlock>
                      </>
                    );
                  }}
                </paypalDepositForm.Field>
              </div>

              <SubmitButtonWrapper form={paypalDepositForm}>
                <Button
                  className={styles.primaryButton}
                  color="primary"
                  data-testid="balance-deposit-submit"
                  size="s"
                  type="submit"
                  variant="filled"
                >
                  {t('deposit.createPaypalDeposit')}
                </Button>
              </SubmitButtonWrapper>
            </fieldset>

            {lastPaypalApprovalUrl ? (
              <div className={styles.lastTaskRow}>
                <TextBlock color="secondary" size="s">
                  {t('deposit.paypalManualContinue')}
                </TextBlock>
                <a
                  className={styles.payLink}
                  data-testid="deposit-pay-link"
                  href={lastPaypalApprovalUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t('deposit.openPaypalApproval')}
                </a>
              </div>
            ) : null}
          </form>
        ) : isCardDeposit ? (
          <form
            className={cx(
              styles.formCard,
              isDepositBlockedByAge && styles.blockedForm,
            )}
            data-testid="deposit-form"
            onSubmit={(e) => {
              e.preventDefault();
              void cardDepositForm.handleSubmit();
            }}
          >
            <fieldset
              className={styles.formFieldset}
              disabled={isDepositBlockedByAge}
            >
              <DepositAmountSection
                amountLabel={t('deposit.amountUsd')}
                disabled={isDepositBlockedByAge}
                form={cardDepositForm as TDepositAmountForm}
                hasAmountError={hasDepositAmountError}
                minimumDepositLabel={minimumDepositLabel}
                onAmountChange={clearDepositAmountError}
              />

              {paymentTypeField}

              <div className={styles.field}>
                <TextBlock color="contrast" weight="600">
                  {t('deposit.totalWithFee')}
                </TextBlock>
                <cardDepositForm.Field name="coins">
                  {(field) => {
                    const totalCoinsWithServiceFee =
                      calculateDodoTotalWithServiceFeeCoins(
                        toCoinsNumber(field.state.value),
                      );

                    return (
                      <>
                        <TextBlock
                          color="contrast"
                          data-testid="balance-deposit-total-display"
                          size="xl"
                          weight="600"
                        >
                          {formatMoney(totalCoinsWithServiceFee, {
                            nullValue: '$0.00',
                          })}
                        </TextBlock>
                        <TextBlock
                          color="secondary"
                          data-testid="balance-deposit-total-note"
                          size="s"
                        >
                          {t('deposit.totalFeeNote')}
                        </TextBlock>
                      </>
                    );
                  }}
                </cardDepositForm.Field>
              </div>

              <CardDepositFormSection
                disabled={isDepositBlockedByAge}
                form={cardDepositForm}
              />

              <SubmitButtonWrapper form={cardDepositForm}>
                <Button
                  className={styles.primaryButton}
                  color="primary"
                  data-testid="balance-deposit-submit"
                  size="s"
                  type="submit"
                  variant="filled"
                >
                  {t('deposit.createCardDeposit')}
                </Button>
              </SubmitButtonWrapper>
            </fieldset>

            {lastCardPaymentUrl ? (
              <div className={styles.lastTaskRow}>
                <TextBlock color="secondary" size="s">
                  {t('deposit.cardManualContinue')}
                </TextBlock>
                <a
                  className={styles.payLink}
                  data-testid="deposit-pay-link"
                  href={lastCardPaymentUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t('deposit.openCardAuthentication')}
                </a>
              </div>
            ) : null}
          </form>
        ) : isDodoDeposit ? (
          <form
            className={cx(
              styles.formCard,
              isDepositBlockedByAge && styles.blockedForm,
            )}
            data-testid="deposit-form"
            onSubmit={(e) => {
              e.preventDefault();
              void dodoDepositForm.handleSubmit();
            }}
          >
            <fieldset
              className={styles.formFieldset}
              disabled={isDepositBlockedByAge}
            >
              <DepositAmountSection
                amountLabel={t('deposit.amountUsd')}
                disabled={isDepositBlockedByAge}
                form={dodoDepositForm as TDepositAmountForm}
                hasAmountError={hasDepositAmountError}
                minimumDepositLabel={minimumDepositLabel}
                onAmountChange={clearDepositAmountError}
              />

              {paymentTypeField}

              <div className={styles.field}>
                <TextBlock color="contrast" weight="600">
                  {t('deposit.totalWithFee')}
                </TextBlock>
                <dodoDepositForm.Field name="coins">
                  {(field) => {
                    const totalCoinsWithServiceFee =
                      calculateDodoTotalWithServiceFeeCoins(
                        toCoinsNumber(field.state.value),
                      );

                    return (
                      <>
                        <TextBlock
                          color="contrast"
                          data-testid="balance-deposit-total-display"
                          size="xl"
                          weight="600"
                        >
                          {formatMoney(totalCoinsWithServiceFee, {
                            nullValue: '$0.00',
                          })}
                        </TextBlock>
                        <TextBlock
                          color="secondary"
                          data-testid="balance-deposit-total-note"
                          size="s"
                        >
                          {t('deposit.totalFeeNote')}
                        </TextBlock>
                      </>
                    );
                  }}
                </dodoDepositForm.Field>
              </div>

              <SubmitButtonWrapper form={dodoDepositForm}>
                <Button
                  className={styles.primaryButton}
                  color="primary"
                  data-testid="balance-deposit-submit"
                  size="s"
                  type="submit"
                  variant="filled"
                >
                  {t(selectedPaymentOption.submitKey)}
                </Button>
              </SubmitButtonWrapper>
            </fieldset>

            {lastDodoPaymentUrl ? (
              <div className={styles.lastTaskRow}>
                <TextBlock color="secondary" size="s">
                  {t('deposit.dodoManualContinue')}
                </TextBlock>
                <a
                  className={styles.payLink}
                  data-testid="deposit-pay-link"
                  href={lastDodoPaymentUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t('deposit.openDodoCheckout')}
                </a>
              </div>
            ) : null}
          </form>
        ) : (
          <form
            className={cx(
              styles.formCard,
              isDepositBlockedByAge && styles.blockedForm,
            )}
            data-testid="deposit-form"
            onSubmit={(e) => {
              e.preventDefault();
              void depositForm.handleSubmit();
            }}
          >
            <fieldset
              className={styles.formFieldset}
              disabled={isDepositBlockedByAge}
            >
              <DepositAmountSection
                amountLabel={t('deposit.amountUsdt')}
                disabled={isDepositBlockedByAge}
                form={depositForm as TDepositAmountForm}
                hasAmountError={hasDepositAmountError}
                minimumDepositLabel={minimumDepositLabel}
                onAmountChange={clearDepositAmountError}
              />

              {paymentTypeField}

              <div className={styles.field}>
                <TextBlock color="contrast" weight="600">
                  {t('deposit.network')}
                </TextBlock>
                <FormField form={depositForm} name="currencyCode">
                  <NetworkSelect
                    data-testid="balance-deposit-network-select"
                    options={CRYPTOCLOUD_USDT_NETWORKS}
                  />
                </FormField>
              </div>

              <SubmitButtonWrapper form={depositForm}>
                <Button
                  className={styles.primaryButton}
                  color="primary"
                  data-testid="balance-deposit-submit"
                  size="s"
                  type="submit"
                  variant="filled"
                >
                  {t('deposit.createCryptoDeposit')}
                </Button>
              </SubmitButtonWrapper>
            </fieldset>

            {lastPaymentUrl ? (
              <div className={styles.lastTaskRow}>
                <TextBlock color="secondary" size="s">
                  {t('deposit.cryptoManualContinue')}
                </TextBlock>
                <a
                  className={styles.payLink}
                  data-testid="deposit-pay-link"
                  href={lastPaymentUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t('deposit.openPaymentPage')}
                </a>
              </div>
            ) : null}
          </form>
        )}
      </LayoutCard>

      <Modal
        hideCloseButton
        isAutoClosing={false}
        isOpen={isPaypalCaptureProcessing}
        preventOutsideClose
        title={t('deposit.processingTitle')}
      >
        <div className={styles.field} data-testid="paypal-processing-modal">
          <Spinner />
          <TextBlock color="secondary">{t('deposit.processingText')}</TextBlock>
        </div>
      </Modal>

      <MinimumAgeModal
        isOpen={isMinimumAgeModalOpen}
        minimumAge={minimumAge}
        onClose={closeMinimumAgeModal}
      />
    </AccountLayout>
  );
}
